import { Formik, Form, Field, ErrorMessage } from "formik";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/providers";
import { toast } from "sonner";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { loginSchema } from "@arrowise/shared";

export function LoginForm() {
    const { login, error, clearError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [submitting, setSubmitting] = useState(false);

    // Clear errors when component mounts
    useEffect(() => {
        clearError();
    }, [clearError]);

    return (
        <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={loginSchema}
            onSubmit={async (values, { setSubmitting: setFmSubmitting }) => {
                setSubmitting(true);
                clearError();
                try {
                    await login(values.email, values.password);
                    toast.success("Welcome back!");

                    // Redirect to the page they were trying to access, or default to /app
                    const from = location.state?.from?.pathname || '/app';
                    navigate(from, { replace: true });
                } catch (error: any) {
                    // Show specific error message from the API
                    const errorMessage = error?.message || "Login failed";
                    toast.error(errorMessage);
                } finally {
                    setSubmitting(false);
                    setFmSubmitting(false);
                }
            }}
        >
            {({ isSubmitting }) => (
                <Form className="space-y-4">
                    <div className="space-y-1">
                        <label className="text-sm font-medium" htmlFor="email">Email</label>
                        <Field as={Input} id="email" name="email" type="email" autoComplete="email" />
                        <ErrorMessage name="email" component="div" className="text-xs text-destructive" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium" htmlFor="password">Password</label>
                        <Field as={Input} id="password" name="password" type="password" autoComplete="current-password" />
                        <ErrorMessage name="password" component="div" className="text-xs text-destructive" />
                    </div>
                    {error && <div className="text-xs text-destructive">{error}</div>}
                    <Button type="submit" disabled={isSubmitting || submitting} className="w-full">Login</Button>
                    <div className="space-y-2">
                        <p className="text-xs text-muted-foreground text-center">
                            <Link to="/forgot-password" className="text-primary hover:underline">
                                Forgot your password?
                            </Link>
                        </p>
                        <p className="text-xs text-muted-foreground text-center">
                            Don't have an account? <Link to="/register" className="text-primary hover:underline">Register</Link>
                        </p>
                    </div>
                </Form>
            )}
        </Formik>
    );
}
